import { useAuth0 } from '@auth0/auth0-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * ErrorFallback — used as the fallback UI in the ErrorBoundary.
 * Keeps zero dependencies (no router, no context) so it can render
 * even when the surrounding app has crashed.
 */
const ErrorFallback = ({ error, resetErrorBoundary }) => {
    const { isAuthenticated } = useAuth0();
    const navigate = useNavigate();
    const handleReload = () => {
        if (resetErrorBoundary) {
            resetErrorBoundary();
        } else {
            window.location.reload();
        }
    };

    const handleGoHome = () => {
        window.location.href = isAuthenticated ? '/dashboard' : '/';
    };

    return (
        <section className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
            <div className="max-w-lg w-full text-center">
                {/* Icon */}
                <div className="mx-auto mb-8 w-24 h-24 rounded-2xl bg-red-50 flex items-center justify-center shadow-sm border border-red-100">
                    <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                </div>

                <h1 className="text-3xl font-bold text-gray-900 mb-3">Something Went Wrong</h1>
                <p className="text-gray-500 mb-6 leading-relaxed">
                    An unexpected error occurred. Our team has been notified.<br />
                    Try refreshing the page — most issues resolve on their own.
                </p>

                {/* Collapsible error detail */}
                {error?.message && (
                    <details className="mb-6 text-left bg-red-50 border border-red-100 rounded-lg p-4 cursor-pointer group">
                        <summary className="text-sm font-medium text-red-700 list-none flex items-center gap-2 select-none">
                            <svg className="w-4 h-4 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            Error details
                        </summary>
                        <p className="mt-3 text-xs text-red-600 font-mono break-all whitespace-pre-wrap leading-relaxed">
                            {error.message}
                        </p>
                    </details>
                )}

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                        id="error-fallback-reload-btn"
                        onClick={handleReload}
                        className="bg-primary hover:bg-primary-hover px-6 py-2.5 text-white rounded-lg text-sm font-medium transition-all shadow-xs"
                    >
                        Reload Page
                    </button>
                    <button
                        id="error-fallback-home-btn"
                        onClick={handleGoHome}
                        className="px-6 py-2.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-100 transition-all"
                    >
                        {isAuthenticated ? 'Go to Dashboard' : 'Go to Home'}
                    </button>
                </div>

                <p className="mt-8 text-xs text-gray-400">
                    If the problem persists, please contact support.
                </p>
            </div>
        </section>
    );
};

export default ErrorFallback;
